import React from 'react';
import { makeStyles } from '@mui/styles';
import { Theme } from '@quillbot/design-system';

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    padding: '54px 0 16px',
    maxWidth: 665,
    margin: '0 auto',
    [theme.breakpoints.down('sm')]: {
      padding: '32px 15px 0',
    },
  },
  title: {
    fontSize: 20,
    fontWeight: 600,
    fontStyle: 'italic',
    marginBottom: 64,
    textAlign: 'center',
    [theme.breakpoints.down('sm')]: {
      padding: '0 32px',
    },
  },
  quotesWrapper: {
    display: 'flex',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
    },
  },
  originalWrapper: {},
  paraphrasedWrapper: {
    marginTop: 52,
    marginLeft: -44,
    [theme.breakpoints.down('sm')]: {
      marginTop: -16,
      marginLeft: 36,
    },
  },
  quoteItem: {
    padding: '28px 28px 28px 36px',
    [theme.breakpoints.down('sm')]: {
      padding: '24px 24px 20px 36px',
    },
  },
  quotesOriginal: {
    color: theme.specialColors.ICON_DEFAULT_COLOR,
    width: 364,
    backgroundImage: 'url("../images/theme/commonImages/paraphraserPillar/quote-grey-bg.svg")',
    backgroundSize: 'cover',
    paddingBottom: 78,
    [theme.breakpoints.down('sm')]: {
      paddingBottom: 70,
      width: 350,
    },
  },
  quotesParaphrased: {
    color: theme.palette.primary.main,
    width: 313,
    backgroundImage: 'url("../images/theme/commonImages/paraphraserPillar/quote-green-bg.svg")',
    backgroundSize: 'cover',
    paddingBottom: 90,
    [theme.breakpoints.down('sm')]: {
      width: 310,
    },
  },
  quoteTop: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  quoteImg: {
    width: 38,
  },
  originalImg: {
    '& path': {
      fill: theme.specialColors.ICON_DEFAULT_COLOR,
    },
  },
  paraphrasedImg: {
    '& path': {
      fill: theme.palette.primary.main,
    },
  },
  quoteLabel: {
    fontWeight: 600,
    textTransform: 'uppercase',
  },
  quoteText: {},
  originalText: {
    paddingRight: 16,
    fontSize: 13,
    marginTop: 24,
    [theme.breakpoints.down('sm')]: {
      fontSize: 12.5,
    },
  },
  paraphrasedText: {
    fontWeight: 700,
    fontSize: 20,
    lineHeight: '27px',
    fontStyle: 'italic',
    marginTop: 28,
    paddingRight: 10,
  },
}));

function Quotes() {
  const classes = useStyles();

  return (
    <div className={classes.root}>
      <div className={classes.title}>
        Paraphrasing is about describing something without quoting it exactly, giving a more general
        description of what was presented instead.
      </div>
      <div className={classes.quotesWrapper}>
        <div className={classes.originalWrapper}>
          <div className={`${classes.quoteItem} ${classes.quotesOriginal}`}>
            <div className={classes.quoteTop}>
              <svg
                className={`${classes.quoteImg} ${classes.originalImg}`}
                width="38"
                height="34"
                viewBox="0 0 38 34"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M6.94365 17.2077C6.94365 18.4191 7.65771 19.063 9.08584 19.1332H11.3364C12.9112 19.2097 14.2819 19.9237 15.4487 21.2754C16.609 22.627 17.1956 24.1699 17.1956 25.8913C17.1956 27.9952 16.4433 29.7612 14.945 31.183C13.4404 32.6111 11.5659 33.3252 9.31536 33.3252C6.68862 33.3252 4.5273 32.2924 2.83777 30.2267C1.14825 28.1674 0.300293 25.5215 0.300293 22.2891C0.300293 16.2833 2.66563 10.1245 7.39631 3.81264C9.1241 1.49193 10.6224 0.325195 11.9038 0.325195C13.332 0.325195 14.046 1.08389 14.046 2.5949C14.046 3.20058 13.6316 4.02941 12.8092 5.08775C11.3045 6.9813 9.95292 9.16812 8.75431 11.661C7.54933 14.1538 6.94365 16.0027 6.94365 17.2077ZM27.4475 17.2077C27.4475 18.4191 28.1616 19.063 29.5897 19.1332H31.8403C33.3386 19.2097 34.671 19.9237 35.8378 21.2754C36.9981 22.627 37.5847 24.1699 37.5847 25.8913C37.5847 27.9952 36.8324 29.7612 35.3341 31.183C33.8295 32.6111 31.955 33.3252 29.7045 33.3252C27.0777 33.3252 24.9164 32.2924 23.2269 30.2267C21.5374 28.161 20.6894 25.5151 20.6894 22.2827C20.6894 16.2769 23.0547 10.1181 27.7854 3.80626C29.5132 1.49193 31.0178 0.325195 32.293 0.325195C33.7211 0.325195 34.4352 1.08389 34.4352 2.5949C34.4352 3.20058 34.0207 4.02941 33.1983 5.08775C31.1709 7.65711 29.4813 10.7174 28.1297 14.2686C27.677 15.4736 27.4475 16.4554 27.4475 17.2077Z"
                  fill="#4C9459"
                />
              </svg>
              <div className={classes.quoteLabel}>Original</div>
            </div>
            <div className={`${classes.quoteText} ${classes.originalText}`}>
              “The weather was very rainy, so he decided to pack accordingly. He brought rain boots,
              an umbrella, and a waterproof jacket. Although there was heavy cloud cover, the news
              said it was going to lighten up by the afternoon, so he packed his favorite
              sunglasses.”
            </div>
          </div>
        </div>
        <div className={classes.paraphrasedWrapper}>
          <div className={`${classes.quoteItem} ${classes.quotesParaphrased}`}>
            <div className={classes.quoteTop}>
              <svg
                className={`${classes.quoteImg} ${classes.paraphrasedImg}`}
                width="38"
                height="34"
                viewBox="0 0 38 34"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M6.94365 17.2077C6.94365 18.4191 7.65771 19.063 9.08584 19.1332H11.3364C12.9112 19.2097 14.2819 19.9237 15.4487 21.2754C16.609 22.627 17.1956 24.1699 17.1956 25.8913C17.1956 27.9952 16.4433 29.7612 14.945 31.183C13.4404 32.6111 11.5659 33.3252 9.31536 33.3252C6.68862 33.3252 4.5273 32.2924 2.83777 30.2267C1.14825 28.1674 0.300293 25.5215 0.300293 22.2891C0.300293 16.2833 2.66563 10.1245 7.39631 3.81264C9.1241 1.49193 10.6224 0.325195 11.9038 0.325195C13.332 0.325195 14.046 1.08389 14.046 2.5949C14.046 3.20058 13.6316 4.02941 12.8092 5.08775C11.3045 6.9813 9.95292 9.16812 8.75431 11.661C7.54933 14.1538 6.94365 16.0027 6.94365 17.2077ZM27.4475 17.2077C27.4475 18.4191 28.1616 19.063 29.5897 19.1332H31.8403C33.3386 19.2097 34.671 19.9237 35.8378 21.2754C36.9981 22.627 37.5847 24.1699 37.5847 25.8913C37.5847 27.9952 36.8324 29.7612 35.3341 31.183C33.8295 32.6111 31.955 33.3252 29.7045 33.3252C27.0777 33.3252 24.9164 32.2924 23.2269 30.2267C21.5374 28.161 20.6894 25.5151 20.6894 22.2827C20.6894 16.2769 23.0547 10.1181 27.7854 3.80626C29.5132 1.49193 31.0178 0.325195 32.293 0.325195C33.7211 0.325195 34.4352 1.08389 34.4352 2.5949C34.4352 3.20058 34.0207 4.02941 33.1983 5.08775C31.1709 7.65711 29.4813 10.7174 28.1297 14.2686C27.677 15.4736 27.4475 16.4554 27.4475 17.2077Z"
                  fill="#4C9459"
                />
              </svg>
              <div className={classes.quoteLabel}>Paraphrase</div>
            </div>
            <div className={`${classes.quoteText} ${classes.paraphrasedText}`}>
              “He packed ahead for the rainy day, and he also brought sunglasses in case it got
              sunny.”
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Quotes;
